import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from "react";
import clsx from "clsx";
import { Loader2 } from "lucide-react";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
/** sm 32 (admin), md 40 (admin default), touch 44 (portal), lg 48 (portal primary on phone). */
export type ButtonSize = "sm" | "md" | "touch" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: ReactNode;
}

export const buttonClass = (variant: ButtonVariant = "primary", size: ButtonSize = "md", className?: string) =>
  clsx(`iga-btn-${variant}`,
    size === "sm" && "control-sm px-3 text-[13px]",
    size === "md" && "control-md",
    size === "touch" && "control-touch",
    size === "lg" && "control-lg px-5 text-[15px]",
    className);

/**
 * Button (§3). Labels are verb + object ("Apply 1,204 changes"). One primary per view; danger only inside a
 * confirmation or a "Danger zone". `type` defaults to "button" so buttons inside forms do not submit by accident.
 */
export function Button({ variant = "primary", size = "md", loading, icon, className, children, disabled, type = "button", ...rest }: ButtonProps) {
  return (
    <button type={type} className={buttonClass(variant, size, className)} disabled={disabled || loading} aria-busy={loading || undefined} {...rest}>
      {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : icon}
      {children}
    </button>
  );
}

/** Link styled as a button (page actions that navigate). */
export function ButtonLink({ variant = "secondary", size = "md", icon, className, children, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement> & { variant?: ButtonVariant; size?: ButtonSize; icon?: ReactNode }) {
  return <a className={buttonClass(variant, size, className)} {...rest}>{icon}{children}</a>;
}
